import Phaser from 'phaser';
import {
    TILE_SIZE, PLAYER_SPEED, GAME_WIDTH, GAME_HEIGHT
} from '../config/game.config';
import { MazeGenerator, TileType } from '../systems/MazeGenerator';
import { networkManager, SessionMessages } from '../network/NetworkManager';
import { HTMLTouchControls } from '../ui/HTMLTouchControls';
import { Player, PlayerEvents } from '../entities/Player';
import { HTMLHUD } from '../ui/HTMLHUD';
import { MovementController } from '../systems/MovementController';
import { GameStateManager } from '../systems/GameStateManager';
import { MultiplayerSync } from '../systems/MultiplayerSync';
import { PlayerListUI } from '../ui/PlayerListUI';
import { StatsTracker } from '../systems/StatsTracker';
import { SpectatorSystem } from '../systems/SpectatorSystem';
import { DeathReplaySystem } from '../systems/DeathReplaySystem';
import { SessionManager } from '../systems/SessionManager';
import { createIndependentSystems, createVisualSystems } from '../systems/SystemFactory';
import { FartSystem } from '../systems/FartSystem';
import { DamageSystem } from '../systems/DamageSystem';
import { CameraController } from '../systems/CameraController';
import { PickupSystem } from '../systems/PickupSystem';
import { FartSelectionHUD } from '../ui/FartSelectionHUD';
import { isDebugLevel, getDebugPlayer } from '../utils/DebugConfig';
import { FartType } from '../entities/farts';
import { HTMLMinimap } from '../ui/HTMLMinimap';

/**
 * GameScene - Main gameplay scene with multiplayer support
 */
export class GameScene extends Phaser.Scene {
    private player!: Player;
    private remotePlayers: Map<string, Player> = new Map();
    private farts!: Phaser.Physics.Arcade.Group;

    private touchControls?: HTMLTouchControls;
    private walls!: Phaser.Physics.Arcade.StaticGroup;
    private mazeData!: TileType[][];

    // HUD
    private hud!: HTMLHUD;

    // Controllers
    private movementController!: MovementController;
    private gameStateManager!: GameStateManager;
    private multiplayerSync!: MultiplayerSync;
    private playerListUI!: PlayerListUI;
    private statsTracker!: StatsTracker;
    private spectatorSystem!: SpectatorSystem;
    private deathReplaySystem!: DeathReplaySystem;
    private sessionManager!: SessionManager;
    private fartSystem!: FartSystem;
    private damageSystem!: DamageSystem;
    private cameraController!: CameraController;
    private pickupSystem!: PickupSystem;
    private fartSelectionHUD?: FartSelectionHUD;
    private minimap!: HTMLMinimap;

    // Multiplayer
    private isMultiplayer: boolean = false;
    private mazeSeed: number = Date.now();

    constructor() {
        super({ key: 'GameScene' });
    }

    private mazeCfg: { width: number, height: number } = { width: 25, height: 18 };

    init(data: { multiplayer?: boolean; seed?: number; players?: string[], width?: number, height?: number }): void {
        this.isMultiplayer = data.multiplayer ?? false;
        this.mazeSeed = data.seed ?? Date.now();
        if (data.width && data.height) {
            this.mazeCfg = { width: data.width, height: data.height };
        }
        this.remotePlayers.clear();
    }

    create(): void {
        this.cameras.main.fadeIn(300);

        // Generate maze (using synchronized dimensions)
        const mazeGenerator = new MazeGenerator();

        // Check for debug level mode
        if (isDebugLevel()) {
            // Use simple open arena for debugging
            this.mazeData = mazeGenerator.generateDebugLevel(this.mazeCfg.width, this.mazeCfg.height);
        } else {
            this.mazeData = mazeGenerator.generate(this.mazeCfg.width, this.mazeCfg.height, this.mazeSeed);
        }

        // Create tile groups
        this.walls = this.physics.add.staticGroup();
        this.farts = this.physics.add.group();

        // Render maze
        this.renderMaze();

        // Initialize GameStateManager first (needed by MultiplayerSync)
        // Note: player is set after createPlayer call below

        // Initialize MultiplayerSync (needs mazeData and walls)
        // GameStateManager is passed later via setter after player is created
        this.multiplayerSync = new MultiplayerSync(
            this,
            this.remotePlayers,
            this.walls,
            this.farts,
            this.mazeData
        );

        // Find spawn points through sync system
        const spawnPoints = this.multiplayerSync.findAllSpawnPoints();

        // Create local player at first available spawn
        const mySpawnIndex = this.multiplayerSync.getMySpawnIndex();
        const mySpawn = spawnPoints[mySpawnIndex] || spawnPoints[0];
        this.createPlayer(mySpawn.x, mySpawn.y, mySpawnIndex);

        // Set player reference in sync system
        this.multiplayerSync.setPlayer(this.player);
        this.multiplayerSync.setup();

        // Set up input and movement controller
        this.movementController = new MovementController(this, this.player, this.mazeData);

        // Initialize independent systems via factory
        const independentSystems = createIndependentSystems();
        this.playerListUI = independentSystems.playerListUI;
        this.statsTracker = independentSystems.statsTracker;
        this.sessionManager = independentSystems.sessionManager;

        // Initialize visual systems via factory
        const visualSystems = createVisualSystems(this);
        this.spectatorSystem = visualSystems.spectatorSystem;
        this.deathReplaySystem = visualSystems.deathReplaySystem;

        // Initialize FartSystem and DamageSystem
        this.fartSystem = new FartSystem(this, this.player, this.farts, this.statsTracker);
        this.fartSystem.setOnBroadcast((x, y, chargePercent, fartType, direction) => {
            if (this.isMultiplayer) {
                this.multiplayerSync.broadcastChargedFart(x, y, chargePercent, fartType, direction);
            }
        });

        // Set up player list for ghost fart AI targeting
        const allPlayers = [this.player, ...Array.from(this.remotePlayers.values())];
        this.fartSystem.setPlayers(allPlayers);

        // Set maze data for SBD wall collision detection
        this.fartSystem.setMazeData(this.mazeData);

        this.damageSystem = new DamageSystem(
            this,
            this.player,
            this.farts,
            this.mazeData,
            this.statsTracker,
            this.deathReplaySystem,
            this.isMultiplayer
        );

        // Now create GameStateManager with statsTracker available
        this.gameStateManager = new GameStateManager(this, this.player, this.remotePlayers);
        this.gameStateManager.setStatsTracker(this.statsTracker);

        // Now set GameStateManager on MultiplayerSync (deferred to avoid circular init)
        this.multiplayerSync.setGameStateManager(this.gameStateManager);

        // Set multiplayer sync on damage system for position broadcasting
        this.damageSystem.setMultiplayerSync(this.multiplayerSync);

        // Initialize Pickup System
        this.pickupSystem = new PickupSystem(
            this,
            this.player,
            this.fartSystem.getInventory(),
            this.mazeData,
            spawnPoints,
            { minPickups: 4, maxPickups: 8, respawnInterval: 25000 },
            this.isMultiplayer
        );
        this.pickupSystem.spawnInitialPickups(this.mazeSeed);
        this.pickupSystem.onCollect(() => {
            this.fartSelectionHUD?.update();
            this.touchControls?.updateFartButtonDisplay();
        });

        // Initialize Touch Controls if on a touch device
        if (this.sys.game.device.input.touch) {
            this.touchControls = new HTMLTouchControls();
            this.touchControls.create();
            this.movementController.setTouchControls(this.touchControls);

            // Connect inventory for fart type selection
            this.touchControls.setInventory(this.fartSystem.getInventory());

            this.touchControls.onFartStart(() => {
                if (!this.player.isCharging) {
                    this.player.startCharging();
                    if (this.isMultiplayer) this.multiplayerSync.broadcastChargeStart();
                }
            });
            this.touchControls.onFartRelease(() => {
                if (this.player.isCharging) this.fartSystem.releaseChargedFart();
                // Update display after firing (ammo may have changed)
                this.touchControls?.updateFartButtonDisplay();
            });
            this.touchControls.onFartCancel(() => {
                if (this.player.isCharging) this.player.stopCharging();
            });
            this.touchControls.onFartTypeChanged((type) => {
                console.log(`Switched to: ${type}`);
            });
        }

        this.sessionManager.init(networkManager.playerId, Array.from(this.remotePlayers.keys()));
        this.statsTracker.initPlayer(networkManager.playerId);
        this.playerListUI.addPlayer(networkManager.playerId, 'You', this.player.color);

        this.remotePlayers.forEach((p, id) => {
            this.statsTracker.initPlayer(id);
            this.playerListUI.addPlayer(id, id.slice(-4), p.color);
        });

        // Fart keys - use SPACE or TAB for all players
        const fartKeys = [
            this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE),
            this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.TAB)
        ];

        fartKeys.forEach(fartKey => {
            fartKey.on('down', () => {
                if (!this.player.isCharging) {
                    this.player.startCharging();
                    if (this.isMultiplayer) this.multiplayerSync.broadcastChargeStart();
                }
            });
            fartKey.on('up', () => {
                if (this.player.isCharging) {
                    this.fartSystem.releaseChargedFart();
                    this.fartSelectionHUD?.update();
                }
            });
        });

        // Fart type selection keys (1-6)
        const inventory = this.fartSystem.getInventory();

        // Create desktop fart selection HUD (only on non-touch devices)
        if (!this.sys.game.device.input.touch) {
            this.fartSelectionHUD = new FartSelectionHUD(inventory);
        }

        const selectFartType = (type: FartType, key: string) => {
            if (inventory.selectType(type)) {
                console.log(`Selected: ${type}`);
            } else {
                console.log(`No ammo for ${type}!`);
            }
            this.fartSelectionHUD?.update();
            this.touchControls?.updateFartButtonDisplay();
        };

        this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ONE).on('down', () => {
            selectFartType(FartType.BASIC, '1');
        });
        this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.TWO).on('down', () => {
            selectFartType(FartType.LINGERING, '2');
        });
        this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.THREE).on('down', () => {
            selectFartType(FartType.GHOST, '3');
        });
        this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.FOUR).on('down', () => {
            selectFartType(FartType.EXPLOSIVE, '4');
        });
        this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.FIVE).on('down', () => {
            selectFartType(FartType.SBD, '5');
        });
        this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SIX).on('down', () => {
            selectFartType(FartType.PROPULSION, '6');
        });

        // Debug key: G = Give ammo for all types (for testing)
        this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.G).on('down', () => {
            inventory.addAmmo(FartType.LINGERING, 2);
            inventory.addAmmo(FartType.GHOST, 1);
            inventory.addAmmo(FartType.EXPLOSIVE, 1);
            inventory.addAmmo(FartType.SBD, 3);
            inventory.addAmmo(FartType.PROPULSION, 4);
            console.log('DEBUG: Added ammo for all fart types');
            this.fartSelectionHUD?.update();
            this.touchControls?.updateFartButtonDisplay();
        });

        // Q = Previous fart type, E = Next fart type
        this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.Q).on('down', () => {
            inventory.selectPrevious();
            console.log(`Selected: ${inventory.getSelectedType()}`);
            this.fartSelectionHUD?.update();
            this.touchControls?.updateFartButtonDisplay();
        });
        this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.E).on('down', () => {
            inventory.selectNext();
            console.log(`Selected: ${inventory.getSelectedType()}`);
            this.fartSelectionHUD?.update();
            this.touchControls?.updateFartButtonDisplay();
        });

        // Set up collisions
        this.physics.add.collider(this.player, this.walls);
        this.physics.add.collider(Array.from(this.remotePlayers.values()), this.walls);

        // Set up overlapping for damage using DamageSystem
        this.damageSystem.setupPhysicsOverlap();

        // --- Camera setup using CameraController ---
        const isMobile = this.sys.game.device.input.touch;
        this.cameraController = new CameraController(this);
        this.cameraController.setupMainCamera(this.player, {
            mazeWidth: this.mazeCfg.width,
            mazeHeight: this.mazeCfg.height,
            isMobile
        });
        // Note: Phaser camera minimap removed - using HTMLMinimap instead

        // Create HTML-based minimap with fog-of-war
        // pixelScale: how many screen pixels per maze tile (e.g., 4 = each tile is 4x4 pixels)
        this.minimap = new HTMLMinimap(this.mazeData, {
            pixelScale: 4,
            margin: 10,
            revealRadius: 3
        });

        // Add HTML HUD
        this.hud = new HTMLHUD(this.isMultiplayer);

        // Mobile controls already initialized above (lines 90-96)

        // Set up multiplayer networking


        // Event listeners for UI updates
        this.player.on(PlayerEvents.HEALTH_CHANGED, (health: number, maxHealth: number) => {
            if (this.hud) this.hud.updateHealth(health, maxHealth);
        });

        this.player.on(PlayerEvents.CAPACITY_CHANGED, (capacity: number, maxCapacity: number) => {
            if (this.hud) this.hud.updateCapacity(capacity);
        });

        this.player.on(PlayerEvents.DIED, () => {
            this.handleLocalDeath();
        });

        // Register session listeners
        this.setupSessionListeners();

        // Clean up HTML elements when scene shuts down
        this.events.once('shutdown', () => {
            // Clean up controllers
            this.cameraController?.destroy();
            this.multiplayerSync.destroy();
            this.pickupSystem?.destroy();
            this.fartSystem?.destroy();
            this.hud?.destroy();
            this.touchControls?.destroy();
            this.fartSelectionHUD?.destroy();
            this.playerListUI?.destroy();
            this.minimap?.destroy();
            // Clear network listeners for session messages to avoid leaks
            networkManager.unsubscribe(SessionMessages.PLAYER_READY);
            networkManager.unsubscribe(SessionMessages.ROUND_START);
            networkManager.unsubscribe(SessionMessages.PLAYER_LEFT);
        });
    }

    update(time: number, delta: number): void {
        // IMPORTANT: Network sync must continue even when dead (host relay for other players)
        if (this.isMultiplayer) {
            this.multiplayerSync.update(delta);

            // Update remote player health display (for spectators)
            this.remotePlayers.forEach((p, id) => {
                this.playerListUI.updateHealth(id, (p.health / p.maxHealth) * 100);
            });
        }

        // Stop other updates if player is dead
        if (!this.player || this.player.isDead()) return;

        this.player.update(time, delta);

        // Disable movement if stunned
        if (!this.player.isStunned) {
            this.movementController.update();
        } else {
            this.player.setAcceleration(0, 0);
            this.player.setVelocity(this.player.body!.velocity.x * 0.9, this.player.body!.velocity.y * 0.9);
        }

        // Update fart system (handles lingering farts)
        this.fartSystem.update(time, delta);

        // Backup damage check - distance-based to ensure fair damage detection
        // This catches any farts that might slip through physics overlap detection
        this.damageSystem.checkDistanceBasedDamage();

        // Update player list health
        this.playerListUI.updateHealth(networkManager.playerId, (this.player.health / this.player.maxHealth) * 100);

        // Update minimap with player position
        this.minimap.updatePlayerPosition(this.player.x, this.player.y);
    }


    // The following methods have been moved to extracted systems:
    // - releaseChargedFart() -> FartSystem
    // - checkFartDamage() -> DamageSystem.checkDistanceBasedDamage()
    // - setupDamageDetection() -> DamageSystem.setupPhysicsOverlap()
    // - hasLineOfSight() -> DamageSystem.hasLineOfSight()
    // - handleDamage() -> DamageSystem.applyDamage()

    private handleLocalDeath(): void {
        this.statsTracker.recordElimination(networkManager.playerId);
        this.playerListUI.markDead(networkManager.playerId);

        // Hide local HUD
        this.hud.destroy();

        this.gameStateManager.handlePlayerDeath();

        // After the basic death animation, start replay
        this.time.delayedCall(1500, () => {
            this.deathReplaySystem.playLoopingReplay(() => {
                this.enterSpectatorMode();
            });
        });
    }

    private enterSpectatorMode(): void {
        // Show player list is now interactive
        this.playerListUI.setClickable(true, (selectedId) => {
            const target = this.remotePlayers.get(selectedId);
            if (target && target.health > 0) {
                this.spectatorSystem.switchTo(target, selectedId.slice(-4));
            }
        });

        // Auto-spectate first alive player
        let firstAliveId: string | undefined;
        for (const [id, p] of this.remotePlayers) {
            if (p.health > 0) {
                firstAliveId = id;
                break;
            }
        }

        if (firstAliveId !== undefined) {
            const target = this.remotePlayers.get(firstAliveId)!;
            this.spectatorSystem.startSpectating(target, firstAliveId.slice(-4));
        } else {
            // Everyone is dead? Just show stats
            this.handleRoundEnd();
        }
    }

    private handleRoundEnd(): void {
        this.sessionManager.endRound();
        const stats = this.statsTracker.finalize(this.sessionManager.getConnectedPlayerIds());

        // Show game over with stats
        // If I'm alive, it might be victory or something else (sessionManager handles)
        this.gameStateManager.showGameOver(!this.player.isDead(), stats);
    }

    private setupSessionListeners(): void {
        networkManager.subscribe(SessionMessages.PLAYER_READY, (message) => {
            const { playerId } = message.payload as { playerId: string };
            this.sessionManager.setPlayerReady(playerId);
            this.checkHostStartCondition();

            // Host relays to other guests
            if (networkManager.isHost) {
                networkManager.broadcast(SessionMessages.PLAYER_READY, message.payload);
            }
        });

        networkManager.subscribe(SessionMessages.ROUND_START, (message) => {
            const { seed } = message.payload as { seed: number };
            this.scene.restart({ multiplayer: true, seed });
        });

        networkManager.subscribe(SessionMessages.PLAYER_LEFT, (message) => {
            const { playerId } = message.payload as { playerId: string };
            this.sessionManager.removePlayer(playerId);
            // check win condition if round active
            if (this.sessionManager.currentState === 'in_round') {
                this.gameStateManager.checkWinCondition(true);
            }

            // Host relays to other guests
            if (networkManager.isHost) {
                networkManager.broadcast(SessionMessages.PLAYER_LEFT, message.payload);
            }
        });

        this.events.on('local-ready', () => {
            this.sessionManager.setPlayerReady(networkManager.playerId);
            this.checkHostStartCondition();
        });

        // Supplement peer leave logic
        const originalOnPeerLeave = networkManager.onPeerLeave;
        networkManager.onPeerLeave = (peerId) => {
            if (originalOnPeerLeave) originalOnPeerLeave(peerId);
            this.sessionManager.removePlayer(peerId);
            if (this.sessionManager.currentState === 'in_round') {
                this.gameStateManager.checkWinCondition(true);
            }
            this.checkHostStartCondition();
        };
    }

    private checkHostStartCondition(): void {
        if (networkManager.isHost && this.sessionManager.isEveryoneReady()) {
            // Show "Start Next Round" button
            const startBtn = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 120, 'START NEXT ROUND', {
                fontSize: '24px',
                fontFamily: 'Arial Black',
                color: '#ffffff',
                backgroundColor: '#00b894',
                padding: { x: 20, y: 10 }
            }).setOrigin(0.5).setScrollFactor(0).setDepth(400).setInteractive({ useHandCursor: true });

            startBtn.on('pointerup', () => {
                const newSeed = Date.now();
                networkManager.broadcast(SessionMessages.ROUND_START, { seed: newSeed });
                this.scene.restart({ multiplayer: true, seed: newSeed });
            });
        }
    }

    private createPlayer(x: number, y: number, colorIndex?: number): void {
        const color = Player.getOrCreatePlayerColor(colorIndex);
        this.player = new Player(this, x, y, 'player', { color });
        this.player.setDisplaySize(28, 28);
        this.player.setCollideWorldBounds(true);
        this.player.setDrag(800);
        this.player.setMaxVelocity(PLAYER_SPEED);
        this.player.setBounce(0.1);

        // Use circular hitbox smaller than display for smoother corner navigation
        // Texture is 32x32, circle radius 10 = 20px diameter
        // Offset to center: (32 - 20) / 2 = 6 for both X and Y
        const body = this.player.body as Phaser.Physics.Arcade.Body;
        body.setCircle(10, 6, 6);
    }

    private renderMaze(): void {
        for (let y = 0; y < this.mazeData.length; y++) {
            for (let x = 0; x < this.mazeData[y].length; x++) {
                const tile = this.mazeData[y][x];
                const worldX = x * TILE_SIZE + TILE_SIZE / 2;
                const worldY = y * TILE_SIZE + TILE_SIZE / 2;

                switch (tile) {
                    case TileType.WALL:
                        const wall = this.walls.create(worldX, worldY, 'wall');
                        wall.setDisplaySize(TILE_SIZE, TILE_SIZE);
                        wall.refreshBody();
                        wall.setDepth(10);
                        break;
                    case TileType.FLOOR:
                        this.add.image(worldX, worldY, 'floor').setDisplaySize(TILE_SIZE, TILE_SIZE).setDepth(0);
                        break;
                    case TileType.SPAWN:
                        this.add.image(worldX, worldY, 'spawn').setDisplaySize(TILE_SIZE, TILE_SIZE).setDepth(1);
                        break;
                }
            }
        }
    }
}

